/**
 * TypeScript type definitions for MMM Configuration
 * Mirrors the Python Pydantic models from qistrel_mmm/config/schemas.py
 */

// === Global Configuration ===
export type DataFrequency = "D" | "W" | "M";
export type RegressionEngine = "ridge" | "elasticnet";
export type SplitType = "time" | "index";
export type AdstockType = "geometric" | "weibull";
export type SaturationType = "hill" | "none";
export type OptimizerType = "OnePlusOne" | "TwoPointsDE";
export type KPIType = "conversion" | "revenue" | "rate" | "metric" | null;

// === Hyperparameter Range ===
export interface HyperparamRange {
  lower: number;
  upper: number;
  logscale?: boolean;
}

// === Media Channel ===
export type LagMode = "sequential" | "independent";

export interface MediaChannel {
  name: string;
  column: string;  // Spend column (what you control/allocate)
  variable_column?: string | null;  // Impressions/delivery column (what drives impact)
  adstock?: AdstockType;
  saturation?: SaturationType;
  lags?: number[] | null;  // e.g., [0, 7, 14] for specific lag periods (Robyn parity)
  lag_mode?: LagMode;  // "sequential" or "independent" (Robyn-style)
}

// === Control Variable ===
export interface ControlVar {
  name: string;
  column: string;
}

// === Feature Engineering ===
export interface FeatureEngineeringConfig {
  enabled: boolean;
  use_lagged_features?: boolean;
  use_interaction_terms?: boolean;
  lag_periods?: number[];
  control_lag_periods?: number[] | null;
}

// === Calendar Configuration ===
export interface CalendarConfig {
  use_trend?: boolean;
  use_seasonality?: boolean;
  seasonality_periods?: number[];
}

// === Decomposition Configuration ===
export interface DecompositionConfig {
  use_prophet?: boolean;
  allow_fallback?: boolean;  // If true, skip decomposition gracefully if Prophet not installed
  country_holidays?: string | null;
  yearly_seasonality?: boolean;
  weekly_seasonality?: boolean;
  daily_seasonality?: boolean;
}

// === Train/Test Split ===
export interface TrainTestSplit {
  type?: SplitType;
  train_ratio: number;
  val_ratio?: number | null;
  test_ratio?: number | null;
  train_end_date?: string | null;
}

// === Hyperparameters ===
export interface HyperparamsConfig {
  ridge_alpha: HyperparamRange;
  l1_ratio?: number | null;
  train_size?: HyperparamRange | null;
}

// === Objective Weights ===
export interface ObjectiveWeights {
  nrmse?: number;
  decomp_rssd?: number;
  mape_lift?: number;
}

// === Optimization ===
export interface OptimizationConfig {
  budget?: number;
  num_trials?: number;
  iterations_per_trial?: number;
  optimizer?: OptimizerType;
  objective_weights?: ObjectiveWeights;
  multi_objective?: boolean;
}

// === Lift Test ===
export interface LiftTest {
  channel: string;
  observed_lift: number;
  confidence_interval?: number;
  sample_size?: number | null;
}

// === Lift Tests Configuration ===
export interface LiftTestConfig {
  enabled?: boolean;
  lift_tests?: LiftTest[];
}

// === NRMSE Method ===
export type NRMSEMethod = "range" | "mean";

// === Complete MMM Configuration ===
export interface MMMConfig {
  // Global
  dep_var: string;
  dep_var_label?: string | null;  // Friendly label for dependent variable (e.g., "Conversions")
  date_var: string;
  frequency?: DataFrequency;
  regression_engine?: RegressionEngine;
  nrmse_method?: NRMSEMethod;  // 'range' (default, qistrel) or 'mean' (Robyn style)
  data_path?: string | null;

  // KPI Scaling (for handling different response variable types)
  kpi_type?: KPIType;  // Auto-detect if null: "conversion", "revenue", "rate", "metric"
  scale_factor?: number | null;  // Auto-calculate if null

  // Media & Control
  media_channels: MediaChannel[];
  control_vars?: ControlVar[];

  // Feature Engineering
  feature_engineering?: FeatureEngineeringConfig;

  // Calendar & Decomposition
  calendar?: CalendarConfig;
  decomposition?: DecompositionConfig;

  // Training
  train_test_split?: TrainTestSplit;

  // Hyperparameters & Optimization
  hyperparams: HyperparamsConfig;
  optimization?: OptimizationConfig;

  // Lift Tests
  lift_tests?: LiftTestConfig;
}

// === Form State (includes UI metadata) ===
export interface MMMConfigFormState extends MMMConfig {
  isAdvancedMode?: boolean;
  _isValid?: boolean;
  _isDirty?: boolean;
  _errors?: Record<string, string[]>;
  _touched?: Record<string, boolean>;
  _collapsedSections?: Record<string, boolean>;
  _saveProgress?: "idle" | "saving" | "saved" | "error";
  _saveError?: string | null;
}

// === Validation Error ===
export interface ValidationError {
  field: string;
  message: string;
  type: "required" | "type" | "custom" | "range";
}

// === API Response Types ===
export interface ConfigSaveResponse {
  success: boolean;
  config_id?: string;
  message?: string;
  errors?: ValidationError[];
}

export interface ConfigLoadResponse {
  success: boolean;
  config: MMMConfig;
  errors?: ValidationError[];
}

export interface DataColumnsResponse {
  success: boolean;
  columns: string[];
  dtypes: Record<string, string>;
  sample_rows?: number;
}

// === UI Helper Types ===
export interface SelectOption<T = string> {
  value: T;
  label: string;
  description?: string;
}

export interface RadioOption<T = string> extends SelectOption<T> { }

export interface FormFieldProps {
  name: string;
  label: string;
  required?: boolean;
  error?: string;
  helperText?: string;
  className?: string;
}

export interface NumberRange {
  min?: number;
  max?: number;
  step?: number;
}

// === Constants ===
export const COUNTRY_OPTIONS: SelectOption[] = [
  { value: "", label: "None" },
  { value: "US", label: "United States" },
  { value: "UK", label: "United Kingdom" },
  { value: "CA", label: "Canada" },
  { value: "AU", label: "Australia" },
  { value: "DE", label: "Germany" },
  { value: "FR", label: "France" },
  { value: "JP", label: "Japan" },
  { value: "IN", label: "India" },
  { value: "BR", label: "Brazil" },
  { value: "MX", label: "Mexico" },
];

export const CONFIDENCE_INTERVALS = [
  { value: 0.9, label: "90% CI" },
  { value: 0.95, label: "95% CI (Recommended)" },
  { value: 0.99, label: "99% CI" },
];

export const FREQUENCY_OPTIONS: RadioOption<DataFrequency>[] = [
  { value: "D", label: "Daily", description: "Data collected daily" },
  { value: "W", label: "Weekly", description: "Data collected weekly (Recommended)" },
  { value: "M", label: "Monthly", description: "Data collected monthly" },
];

export const REGRESSION_ENGINE_OPTIONS: RadioOption<RegressionEngine>[] = [
  {
    value: "ridge",
    label: "Ridge",
    description: "L2 regularization - stable and interpretable (Recommended)",
  },
  {
    value: "elasticnet",
    label: "ElasticNet",
    description: "L1+L2 regularization - can select features",
  },
];

export const ADSTOCK_OPTIONS: RadioOption<AdstockType>[] = [
  {
    value: "geometric",
    label: "Geometric",
    description: "Exponential decay - simpler, more interpretable (Recommended)",
  },
  {
    value: "weibull",
    label: "Weibull",
    description: "S-curve shape - more flexible, needs more data",
  },
];

export const SATURATION_OPTIONS: RadioOption<SaturationType>[] = [
  {
    value: "hill",
    label: "Hill",
    description: "Diminishing returns - typical advertising behavior (Recommended)",
  },
  {
    value: "none",
    label: "None (Linear)",
    description: "Linear impact - only if you're sure",
  },
];

export const OPTIMIZER_OPTIONS: SelectOption<OptimizerType>[] = [
  {
    value: "OnePlusOne",
    label: "OnePlusOne",
    description: "Fast and effective - good balance of speed/quality (Recommended)",
  },
  {
    value: "TwoPointsDE",
    label: "TwoPointsDE",
    description: "Higher quality but 2-3x slower",
  },
];

export const KPI_TYPE_OPTIONS: SelectOption<KPIType>[] = [
  {
    value: null,
    label: "Auto-detect",
    description: "Automatically detect KPI type based on data scale (Recommended)",
  },
  {
    value: "conversion",
    label: "Conversion",
    description: "Conversions, leads, small counts (scales by 10-100x)",
  },
  {
    value: "revenue",
    label: "Revenue",
    description: "Revenue, monetary values (usually no scaling needed)",
  },
  {
    value: "rate",
    label: "Rate",
    description: "CTR, percentages, engagement rates 0-100% (scales by 100-1000x)",
  },
  {
    value: "metric",
    label: "Custom Metric",
    description: "Unknown metric type (auto-scales based on data)",
  },
];

// === Default Values ===
export const DEFAULT_MMM_CONFIG: MMMConfig = {
  dep_var: "",
  date_var: "",
  frequency: "W",
  regression_engine: "ridge",
  nrmse_method: "range",  // 'range' (default) or 'mean' for Robyn compatibility
  kpi_type: null,  // Auto-detect by default
  scale_factor: null,  // Auto-calculate by default
  media_channels: [],
  control_vars: [],
  feature_engineering: {
    enabled: false,
    use_lagged_features: false,
    use_interaction_terms: false,
    lag_periods: [1, 2, 3],
    control_lag_periods: null,
  },
  calendar: {
    use_trend: true,
    use_seasonality: true,
    seasonality_periods: [52],
  },
  decomposition: {
    use_prophet: true,  // Default ON - Prophet handles trend/seasonality via target decomposition
    allow_fallback: true,  // Fall back to Fourier (sin/cos) if Prophet not installed
    country_holidays: null,
    yearly_seasonality: true,
    weekly_seasonality: true,
    daily_seasonality: false,
  },
  train_test_split: {
    type: "time",
    train_ratio: 0.8,
    val_ratio: null,
    test_ratio: null,
    train_end_date: null,
  },
  hyperparams: {
    ridge_alpha: {
      lower: 0.0001,
      upper: 100.0,
      logscale: true,
    },
    l1_ratio: null,
    train_size: null,
  },
  optimization: {
    budget: 2000,
    num_trials: 1,
    iterations_per_trial: 2000,
    optimizer: "OnePlusOne",
    objective_weights: {
      nrmse: 1.0,
      decomp_rssd: 1.0,
      mape_lift: 0.0,
    },
    multi_objective: false,
  },
  lift_tests: {
    enabled: false,
    lift_tests: [],
  },
};

export const DEFAULT_FORM_STATE: MMMConfigFormState = {
  ...DEFAULT_MMM_CONFIG,
  _isValid: false,
  _isDirty: false,
  _errors: {},
  _touched: {},
  _collapsedSections: {
    "feature-engineering": true,
    "lift-tests": true,
  },
  _saveProgress: "idle",
  _saveError: null,
};
